import { createFileRoute, Link } from "@tanstack/react-router";
import { queryOptions, useQuery, useQueryClient, useSuspenseQuery } from "@tanstack/react-query";
import { useState } from "react";
import { toast } from "sonner";

import {
  getImportBatch,
  listImportBatches,
  revertImportBatch,
} from "@/lib/external-bookings.functions";
import { CHANNEL_LABEL, money } from "@/lib/provider-schemas";
import { downloadCsv } from "@/lib/csv-export";
import { ErrorBox, ProviderModuleShell, StatCard } from "@/components/admin/provider/ProviderModuleShell";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";

export const Route = createFileRoute("/_authenticated/admin/import-history")({
  head: () => ({
    meta: [
      { title: "Historial de importaciones — Administración" },
      {
        name: "description",
        content: "Historial de importaciones de Excel/CSV: registros creados, actualizados y reversión.",
      },
      { name: "robots", content: "noindex,nofollow" },
    ],
  }),
  errorComponent: ({ error }) => <ErrorBox error={error as Error} />,
  notFoundComponent: () => <ErrorBox error={new Error("No encontrado")} />,
  component: () => (
    <ProviderModuleShell
      title="Historial de importaciones"
      description="Cada archivo importado, quién lo subió, cuántos registros generó y la posibilidad de revertir solo lo creado por esa importación."
    >
      <Body />
    </ProviderModuleShell>
  ),
});

const batchesQuery = queryOptions({
  queryKey: ["import-batches"],
  queryFn: () => listImportBatches(),
});

function fmtDate(v: string | null | undefined) {
  if (!v) return "—";
  return new Date(v).toISOString().replace("T", " ").slice(0, 16);
}

function Body() {
  const qc = useQueryClient();
  const { data } = useSuspenseQuery(batchesQuery);
  const batches = data.batches as any[];
  const [openId, setOpenId] = useState<string | null>(null);

  const totals = batches.reduce(
    (acc, b) => ({
      lotes: acc.lotes + 1,
      nuevas: acc.nuevas + (b.new_rows ?? 0),
      actualizadas: acc.actualizadas + (b.updated_rows ?? 0),
      errores: acc.errores + (b.error_rows ?? 0),
    }),
    { lotes: 0, nuevas: 0, actualizadas: 0, errores: 0 },
  );

  async function revert(b: any) {
    if (
      !window.confirm(
        `¿Quieres revertir los ${b.new_rows ?? 0} registros creados por esta importación? No se eliminarán reservas anteriores ni reservas con pagos aplicados.`,
      )
    )
      return;
    try {
      const res = await revertImportBatch({ data: { batchId: b.id } });
      toast.success(`Reversión aplicada: ${res.deleted} eliminadas, ${res.kept} conservadas por tener pagos`);
      await qc.invalidateQueries({ queryKey: ["import-batches"] });
      await qc.invalidateQueries({ queryKey: ["external-bookings"] });
    } catch (e) {
      toast.error((e as Error).message);
    }
  }

  return (
    <div className="space-y-4">
      <div className="grid gap-3 sm:grid-cols-4">
        <StatCard label="Importaciones" value={String(totals.lotes)} />
        <StatCard label="Reservas creadas" value={String(totals.nuevas)} />
        <StatCard label="Reservas actualizadas" value={String(totals.actualizadas)} />
        <StatCard label="Filas con error" value={String(totals.errores)} />
      </div>

      <div className="flex flex-wrap gap-2">
        <Button asChild variant="outline" size="sm">
          <Link to="/admin/external-bookings">Ir a reservas externas</Link>
        </Button>
        <Button
          variant="ghost"
          size="sm"
          onClick={() =>
            downloadCsv(
              "historial-importaciones.csv",
              [
                { key: "fecha", label: "Fecha" },
                { key: "archivo", label: "Archivo" },
                { key: "canal", label: "Canal" },
                { key: "total", label: "Registros" },
                { key: "nuevas", label: "Nuevas" },
                { key: "actualizadas", label: "Actualizadas" },
                { key: "duplicadas", label: "Duplicadas" },
                { key: "errores", label: "Errores" },
                { key: "estado", label: "Estado" },
              ],
              batches.map((b) => ({
                fecha: b.created_at,
                archivo: b.file_name,
                canal: b.channel,
                total: b.total_rows,
                nuevas: b.new_rows,
                actualizadas: b.updated_rows,
                duplicadas: b.duplicate_rows,
                errores: b.error_rows,
                estado: b.status,
              })),
            )
          }
        >
          Exportar CSV
        </Button>
      </div>

      <div className="overflow-x-auto rounded-lg border">
        <table className="w-full text-xs">
          <thead className="bg-muted/60">
            <tr>
              <th className="px-3 py-2 text-left">Fecha</th>
              <th className="px-3 py-2 text-left">Archivo</th>
              <th className="px-3 py-2 text-left">Canal</th>
              <th className="px-3 py-2 text-right">Registros</th>
              <th className="px-3 py-2 text-right">Nuevas</th>
              <th className="px-3 py-2 text-right">Actualizadas</th>
              <th className="px-3 py-2 text-right">Duplicadas</th>
              <th className="px-3 py-2 text-right">Errores</th>
              <th className="px-3 py-2 text-left">Estado</th>
              <th className="px-3 py-2" />
            </tr>
          </thead>
          <tbody>
            {batches.length ? (
              batches.map((b) => (
                <tr key={b.id} className="border-t">
                  <td className="px-3 py-2">{fmtDate(b.created_at)}</td>
                  <td className="px-3 py-2">{b.file_name}</td>
                  <td className="px-3 py-2">{CHANNEL_LABEL[b.channel] ?? b.channel}</td>
                  <td className="px-3 py-2 text-right">{b.total_rows}</td>
                  <td className="px-3 py-2 text-right">{b.new_rows}</td>
                  <td className="px-3 py-2 text-right">{b.updated_rows}</td>
                  <td className="px-3 py-2 text-right">{b.duplicate_rows}</td>
                  <td className="px-3 py-2 text-right">{b.error_rows}</td>
                  <td className="px-3 py-2">
                    <Badge variant={b.status === "completed" ? "secondary" : "outline"}>{b.status}</Badge>
                  </td>
                  <td className="px-3 py-2 text-right">
                    <div className="flex justify-end gap-1">
                      <Button size="sm" variant="outline" onClick={() => setOpenId(b.id)}>
                        Revisar
                      </Button>
                      {b.status === "completed" ? (
                        <Button size="sm" variant="ghost" onClick={() => void revert(b)}>
                          Revertir
                        </Button>
                      ) : null}
                    </div>
                  </td>
                </tr>
              ))
            ) : (
              <tr>
                <td colSpan={10} className="px-3 py-8 text-center text-muted-foreground">
                  Todavía no hay importaciones registradas.
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>

      <BatchDialog batchId={openId} onClose={() => setOpenId(null)} />
    </div>
  );
}

function BatchDialog({ batchId, onClose }: { batchId: string | null; onClose: () => void }) {
  const q = useQuery({
    queryKey: ["import-batch", batchId],
    queryFn: () => getImportBatch({ data: { batchId: batchId! } }),
    enabled: !!batchId,
  });
  const batch = q.data?.batch as any;
  const rows = (q.data?.rows ?? []) as any[];

  return (
    <Dialog open={!!batchId} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-h-[88dvh] max-w-4xl overflow-y-auto">
        <DialogHeader>
          <DialogTitle>Detalle de la importación</DialogTitle>
        </DialogHeader>
        {q.isLoading ? (
          <p className="text-sm text-muted-foreground">Cargando…</p>
        ) : batch ? (
          <div className="space-y-4 text-xs">
            <div className="grid gap-2 sm:grid-cols-3">
              <p>
                <span className="text-muted-foreground">Archivo:</span> {batch.file_name}
              </p>
              <p>
                <span className="text-muted-foreground">Canal:</span>{" "}
                {CHANNEL_LABEL[batch.channel] ?? batch.channel}
              </p>
              <p>
                <span className="text-muted-foreground">Fecha:</span> {fmtDate(batch.created_at)}
              </p>
              <p>
                <span className="text-muted-foreground">Nuevas:</span> {batch.new_rows}
              </p>
              <p>
                <span className="text-muted-foreground">Actualizadas:</span> {batch.updated_rows}
              </p>
              <p>
                <span className="text-muted-foreground">Errores:</span> {batch.error_rows}
              </p>
              <p>
                <span className="text-muted-foreground">Archivo original:</span>{" "}
                {batch.file_path ? "guardado" : "no guardado"}
              </p>
              <p>
                <span className="text-muted-foreground">Estado:</span> {batch.status}
              </p>
              <p>
                <span className="text-muted-foreground">Revertida:</span> {fmtDate(batch.reverted_at)}
              </p>
            </div>

            {Array.isArray(batch.errors) && batch.errors.length ? (
              <div className="rounded border border-destructive/40 bg-destructive/10 p-2">
                <p className="font-semibold text-destructive">Filas con error</p>
                <ul className="mt-1 space-y-0.5">
                  {batch.errors.slice(0, 30).map((e: any, i: number) => (
                    <li key={i}>
                      La fila {e.rowNumber}: {(e.errors ?? []).join(", ")}
                    </li>
                  ))}
                </ul>
              </div>
            ) : null}

            <div>
              <p className="mb-1 font-semibold">Reservas creadas por esta importación ({rows.length})</p>
              <div className="max-h-72 overflow-auto rounded border">
                <table className="w-full text-[11px]">
                  <thead className="bg-muted/60">
                    <tr>
                      <th className="px-2 py-1 text-left">Fecha</th>
                      <th className="px-2 py-1 text-left">Tour</th>
                      <th className="px-2 py-1 text-left">Cliente</th>
                      <th className="px-2 py-1 text-right">Venta</th>
                      <th className="px-2 py-1 text-right">Costo</th>
                      <th className="px-2 py-1 text-left">Estado</th>
                    </tr>
                  </thead>
                  <tbody>
                    {rows.map((r) => (
                      <tr key={r.id} className="border-t">
                        <td className="px-2 py-1">{r.service_date ?? "—"}</td>
                        <td className="px-2 py-1">{r.tour_title ?? "—"}</td>
                        <td className="px-2 py-1">
                          {r.customer_first_name ?? ""} {r.customer_last_name ?? ""}
                        </td>
                        <td className="px-2 py-1 text-right">
                          {money(Number(r.sale_total ?? 0), r.sale_currency ?? "MXN")}
                        </td>
                        <td className="px-2 py-1 text-right">
                          {money(Number(r.provider_cost ?? 0), r.cost_currency ?? "MXN")}
                        </td>
                        <td className="px-2 py-1">{r.booking_status}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        ) : (
          <p className="text-sm text-muted-foreground">No encontrado.</p>
        )}
      </DialogContent>
    </Dialog>
  );
}
